<template>
  <div class="reporting">
    <!-- work on filters bar -->
    <div
      class="d-flex bg-white mb-2 p-3 rounded align--center datatable-controls"
    >
      <items-per-page
        :options="perPageOptions"
        :defaultPerPage="10"
        :label="$t('shared.table.per-page')"
        @items-per-page="onLimitChange"
      />
      <button
        class="mx-2 rounded btn btn-micro btn-primary"
        @click="openFilterbar()"
      >
        {{ $t('shared.table.filter-by') }}
      </button>
      <div class="d-flex additionalTableControl">
        <button
          class="rounded btn-with-icon-micro btn btn-micro btn-primary"
          @click="downloadReport"
        >
          <div class="btn-with-icon-content">
            <i class="fa fa-download"></i>
            {{ $t('call-reporting.export') }}
          </div>
        </button>
      </div>
    </div>

    <data-table
      ref="callsTable"
      :data="CDRs"
      :fields="translatedTableFields"
      :pagination="pagination"
      :loading="CDRsLoading"
      @changePage="onPageChange"
    >
      <template v-slot:status="{ row }">
        <call-status :rowData="row"></call-status>
      </template>
      <template v-slot:direction="{ row }">
        <call-direction :rowData="row"></call-direction>
      </template>
      <template v-slot:summary="{ row }">
        <div class="d-flex justify-content-center" v-if="row.callSummary">
          <b-button size="sm" class="p-2 text-sm" @click="showCallSummary(row)">
            <small>{{ $t('call-reporting.table.view-summary') }}</small>
          </b-button>
        </div>
      </template>
      <template v-slot:actions="{ row }">
        <actions :rowData="row"></actions>
      </template>
    </data-table>

    <app-filterbar>
      <template slot="filterbar-content">
        <div class="filterbar-input">
          <!-- {{ filters }} -->
          <!-- <vue-flatpickr-component
            class="vuestic-date-picker"
            v-model="filters.dateRange"
            :config="{
              mode: 'range',
              maxDate: 'today'
            }"
          /> -->
          <DatePicker
            class="vuestic-date-picker"
            :value="filters.dateRange"
            @rangeChange="filters.dateRange = $event"
            :config="{ mode: 'range', maxDate: 'today' }"
          />
        </div>
        <div class="filterbar-input">
          <p class="filter-selector">
            {{ $t('call-reporting.filters.source.title') }}
          </p>

          <Select
            multiple
            searchable
            v-model="filters.sourceExtensions"
            :options="extensionNumbers"
            :preselectFirst="false"
            creatable
            :newTagValidation="newTagValidation"
          />
        </div>

        <div class="filterbar-input">
          <p class="filter-selector">
            {{ $t('call-reporting.filters.destination.title') }}
          </p>

          <Select
            multiple
            searchable
            v-model="filters.destinationExtensions"
            :options="extensionNumbers"
            :preselectFirst="false"
            creatable
            :newTagValidation="newTagValidation"
          />
        </div>

        <div class="filterbar-input">
          <p class="filter-selector">
            {{ $t('call-reporting.filters.tags.title') }}
          </p>

          <Select
            multiple
            searchable
            v-model="filters.tags"
            :options="tagsOptions"
            :preselectFirst="false"
          />
        </div>

        <b-form-group v-slot="{ ariaDescribedby }">
          <b-form-checkbox-group
            class="d-flex flex-column mb-3"
            v-model="filters.callStatuses"
            :aria-describedby="ariaDescribedby"
          >
            <b-form-checkbox value="ANSWERED">
              {{ $t('call-reporting.filters.statuses.answered') }}
            </b-form-checkbox>
            <b-form-checkbox value="FAILED">
              {{ $t('call-reporting.filters.statuses.failed') }}
            </b-form-checkbox>
            <b-form-checkbox value="NO ANSWER">
              {{ $t('call-reporting.filters.statuses.no-answer') }}
            </b-form-checkbox>
            <b-form-checkbox value="BUSY">
              {{ $t('call-reporting.filters.statuses.busy') }}
            </b-form-checkbox>
          </b-form-checkbox-group>
        </b-form-group>

        <div class="w-100 text-center">
          <button
            class="mb-3 btn btn-primary btn-sm filterbar-button"
            @click="applyFilters"
          >
            {{ $t('shared.filterBar.apply-filters') }}
          </button>
          <button
            class="border-0 bg-transparent shadow-none btn btn-primary btn-sm filterbar-button"
            @click="resetFilters"
          >
            {{ $t('shared.filterBar.reset-filters') }}
          </button>
        </div>
      </template>
    </app-filterbar>

    <modal
      v-if="currentRow && currentRow.callSummary"
      :isOpen="callSummaryModalOpen"
      @dismiss="callSummaryModalOpen = false"
    >
      <h4 class="m-0">{{ $t('call-reporting.table.summary-modal.title') }}</h4>
      <div class="mb-4 w-100 comment">
        <label>
          {{ $t('call-reporting.table.summary-modal.comment') }}
        </label>
        <textarea
          class="form-control"
          rows="5"
          :value="currentRow.callSummary.comment"
          readonly
        ></textarea>
      </div>
      <div class="mb-4 w-100 added-by">
        <label>
          {{ $t('call-reporting.table.summary-modal.added-by') }}
        </label>
        <input
          class="form-control"
          :value="currentRow.callSummary.addedBy"
          readonly
        />
      </div>
      <div class="w-100 tags">
        <label class="m-0">
          {{ $t('call-reporting.table.summary-modal.tags') }}
        </label>
        <div class="post-call-tags">
          <b-badge
            class="mx-2"
            variant="primary"
            v-for="tag in currentRow.callSummary.postCallTags"
            :key="tag"
          >
            {{ tag }}
          </b-badge>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import AppFilterbar from '@/components/admin/app-filterbar/AppFilterbar'

import CallStatus from './components/call-status'
import CallDirection from './components/call-direction'
Vue.component('call-status', CallStatus)
Vue.component('call-direction', CallDirection)

import moment from 'moment'
import CallActions from './components/call-actions'
import DataTable from '../../elements/Table.vue'
import Modal from '../../elements/Modal'

import CDRService from '../../services/cdr.service'
import ItemsPerPage from '../../vuestic-theme/vuestic-components/vuestic-datatable/datatable-components/ItemsPerPage.vue'
Vue.component('actions', CallActions)

import DatePicker from '../../elements/DatePicker.vue'
import tagService from '../../services/tags.service'

export default {
  components: {
    AppFilterbar,
    DataTable,
    ItemsPerPage,
    Modal,
    DatePicker
  },
  data() {
    return {
      fields: [
        {
          accessor: 'direction',
          header: 'call-reporting.table.call-direction'
        },
        { accessor: 'status', header: 'call-reporting.table.call-status' },
        {
          accessor: 'call_date_time',
          header: 'call-reporting.table.call-date'
        },
        { accessor: 'duration', header: 'call-reporting.table.call-duration' },
        { accessor: 'source', header: 'call-reporting.table.source' },
        { accessor: 'destination', header: 'call-reporting.table.destination' },
        { accessor: 'summary', header: 'call-reporting.table.summary' },
        { accessor: 'actions', header: 'call-reporting.table.Actions' }
      ],
      CDRs: [],
      pagination: {
        totalPages: 0,
        page: 1,
        perPage: 10
      },
      CDRsLoading: false,
      filters: {
        dateRange: {
          from: moment().format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD')
        },
        callStatuses: [],
        tags: []
      },
      perPageOptions: [
        {
          value: 10
        },
        {
          value: 25
        },
        {
          value: 100
        }
      ],
      callSummaryModalOpen: false,
      currentRow: null,
      tagsOptions: []
    }
  },
  computed: {
    ...mapGetters(['currentTenant', 'allExtensions', 'currentUser']),
    translatedTableFields() {
      return this.fields.map(field => ({
        ...field,
        header: this.$t(field.header)
      }))
    },
    locale() {
      return Vue.i18n.locale()
    },
    extensionNumbers() {
      return this.allExtensions.map(({ ext, name }) => ({
        name: `${name} (${ext})`,
        id: ext
      }))
    }
  },
  methods: {
    ...mapActions(['fetchAllExtensions']),
    onPageChange(page) {
      this.pagination.page = page
      this.getCDRs({ page })
    },
    onLimitChange(limit) {
      this.pagination.limit = limit
      this.getCDRs({ limit })
    },
    onFiltersChange(filters) {
      this.pagination.page = 1
      this.getCDRs({
        filters
      })
    },
    async getCDRs({
      page = this.pagination.page,
      limit = this.pagination.limit,
      filters
    } = {}) {
      this.CDRsLoading = true

      try {
        let allFilters = {
          sourceExtensions:
            (this.filters.sourceExtensions &&
              this.filters.sourceExtensions.map(ext =>
                encodeURIComponent(ext.id)
              )) ||
            '',
          destinationExtensions:
            (this.filters.destinationExtensions &&
              this.filters.destinationExtensions.map(ext =>
                encodeURIComponent(ext.id)
              )) ||
            '',
          fromDate: this.filters.dateRange.from,
          toDate: this.filters.dateRange.to || this.filters.dateRange.from,
          callStatuses: this.filters.callStatuses,
          tags: this.filters.tags.map(tag => tag.id),
          ...filters
        }

        console.log(allFilters)

        const { data, ...pagination } = await CDRService.getCDRs(
          allFilters,
          page,
          limit
        )
        this.CDRs = data
        this.pagination = {
          ...this.pagination,
          ...pagination,
          totalPages: pagination.last_page
        }
      } catch (error) {
        const errorMessage = error.response && error.response.data.message
        this.toast(errorMessage, { type: 'error' })
      } finally {
        this.CDRsLoading = false
      }
    },
    async downloadReport() {
      try {
        this.downloadingReport = true

        let filters = {}
        filters.sourceExtensions =
          (this.filters.sourceExtensions &&
            this.filters.sourceExtensions.map(ext =>
              encodeURIComponent(ext.id)
            )) ||
          ''
        filters.destinationExtensions =
          (this.filters.destinationExtensions &&
            this.filters.destinationExtensions.map(ext =>
              encodeURIComponent(ext.id)
            )) ||
          ''
        filters.fromDate = this.filters.dateRange.from
        filters.toDate =
          this.filters.dateRange.to || this.filters.dateRange.from

        filters.callStatuses = this.filters.callStatuses
        filters.tags = this.filters.tags.map(tag => tag.id)

        await CDRService.exportCDRs({
          ...filters
        })
        this.toast(this.$t('call-reporting.toasts.exported-successfully'))
      } catch (error) {
        console.log(error)
      } finally {
        this.downloadingReport = false
      }
    },
    async openFilterbar() {
      this.$store.dispatch('toggleFilterbar', true)
    },
    applyFilters() {
      let filters = {}
      filters.sourceExtensions =
        (this.filters.sourceExtensions &&
          this.filters.sourceExtensions.map(ext =>
            encodeURIComponent(ext.id)
          )) ||
        ''
      filters.destinationExtensions =
        (this.filters.destinationExtensions &&
          this.filters.destinationExtensions.map(ext =>
            encodeURIComponent(ext.id)
          )) ||
        ''
      filters.fromDate = this.filters.dateRange.from
      filters.toDate = this.filters.dateRange.to || this.filters.dateRange.from

      filters.callStatuses = this.filters.callStatuses
      filters.tags = this.filters.tags.map(tag => tag.id)

      this.onFiltersChange(filters)
    },
    resetFilters() {
      this.filters = {
        fromDate:
          (this.currentTenant && this.currentTenant.isDemo) ||
          (this.currentUser && this.currentUser.role != 'Admin')
            ? moment().format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD'),
        dateRange: {
          from:
            (this.currentTenant && this.currentTenant.isDemo) ||
            (this.currentUser && this.currentUser.role != 'Admin')
              ? moment().format('YYYY-MM-DD')
              : moment().format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD')
        },
        callStatuses: [],
        tags: []
      }
      setTimeout(() => {
        console.log(this.filters)
        this.applyFilters()
      }, 0)
    },
    showCallSummary(row) {
      this.callSummaryModalOpen = true
      this.currentRow = row
    },
    async fetchAllTags() {
      try {
        const res = await tagService.getAllTags()
        const locale = Vue.i18n.locale().toUpperCase()
        this.tagsOptions = res.callTags.map(tag => ({
          id: tag.id,
          name: tag[`name${locale}`]
        }))
      } catch (error) {
        this.toast(error.response.data.message, { type: 'error' })
      }
    },
    newTagValidation(newTag) {
      const regex = /^\+?[0-9]+$/
      if (!regex.test(newTag)) {
        this.toast(this.$t('call-reporting.toasts.invalid-search'), {
          type: 'error'
        })
        return false
      }
      return true
    }
  },
  mounted() {
    if (
      (this.currentTenant && this.currentTenant.isDemo) ||
      (this.currentUser && this.currentUser.role != 'Admin')
    ) {
      this.filters.dateRange = {
        from: moment().format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD')
      }
    }

    this.getCDRs()
    this.fetchAllExtensions()
    this.fetchAllTags()
  },
  watch: {
    currentTenant(currentTenant) {
      if (
        (currentTenant && currentTenant.isDemo) ||
        (this.currentUser && this.currentUser.role != 'Admin')
      ) {
        this.filters.dateRange = {
          from: moment().format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD')
        }
      }
      this.getCDRs()
    }
  }
}
</script>
<style lang="scss" scoped>
h4 {
  margin-top: 0;
}

.form-group {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
</style>
<style lang="scss">
td.with-icon {
  position: relative;
}

.btn.loading {
  animation: loading 1s infinite;
}

@keyframes loading {
  0% {
    background-color: $darkblue;
  }

  25% {
    background-color: $blue;
  }

  50% {
    background-color: $darkblue;
  }

  75% {
    background-color: $blue;
  }

  100% {
    background-color: $darkblue;
  }
}

.reporting {
  .dropdown-menu-content {
    height: 300px;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background-color: #0d4d80;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #fff;
      outline: 0;
    }
  }
}

.post-call-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
